import { useEffect, useState } from 'react';
import type { LineaMaterial, Solicitud } from '../lib/tipos';
import { api, ApiError } from '../lib/api';
import { Aviso, Boton, Campo, Tarjeta } from './ui';

export function PedidoMaterial({ ficha, onCambio }: { ficha: Solicitud; onCambio: () => void }) {
  const [lines, setLines] = useState<LineaMaterial[]>(ficha.materialLines ?? []);
  const [error, setError] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  useEffect(() => setLines(ficha.materialLines ?? []), [ficha.materialLines]);
  if (!ficha.kitchenPlan) return null;
  const editable = ficha.permissions?.editKitchen && ficha.status === 'AUTHORIZED';
  const files = ficha.attachments ?? [];
  const version = ficha.kitchenPlan.version;
  async function guardar(fn: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      onCambio();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No se ha podido guardar el pedido.');
    } finally {
      setBusy(false);
    }
  }
  const change = (index: number, patch: Partial<LineaMaterial>) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const move = (index: number, offset: number) => {
    const next = [...lines];
    const other = index + offset;
    if (other < 0 || other >= next.length) return;
    [next[index], next[other]] = [next[other]!, next[index]!];
    setLines(next);
  };
  return (
    <Tarjeta titulo="Pedido de material">
      <div className="pila">
        {error && <Aviso tono="error">{error}</Aviso>}
        {lines.map((l, i) => (
          <div key={i} className="pila">
            {editable ? (
              <>
                <div className="rejilla tres">
                  <Campo etiqueta="Artículo">
                    <input type="text" value={l.item} onChange={(e) => change(i, { item: e.target.value })} />
                  </Campo>
                  <Campo etiqueta="Cantidad">
                    <input
                      type="number"
                      min="0.001"
                      step="any"
                      value={l.quantity}
                      onChange={(e) => change(i, { quantity: e.target.value })}
                    />
                  </Campo>
                  <Campo etiqueta="Unidad">
                    <select
                      value={l.unit}
                      onChange={(e) => change(i, { unit: e.target.value as LineaMaterial['unit'] })}
                    >
                      {['kg', 'g', 'l', 'ud', 'caja', 'bandeja', 'otro'].map((u) => (
                        <option key={u}>{u}</option>
                      ))}
                    </select>
                  </Campo>
                </div>
                <Campo etiqueta="Observaciones">
                  <input type="text"
                    value={l.notes ?? ''}
                    onChange={(e) => change(i, { notes: e.target.value })}
                  />
                </Campo>
                <div className="fila">
                  <Boton disabled={i === 0} onClick={() => move(i, -1)}>
                    Subir línea
                  </Boton>
                  <Boton disabled={i === lines.length - 1} onClick={() => move(i, 1)}>
                    Bajar línea
                  </Boton>
                  <Boton onClick={() => setLines(lines.filter((_, n) => n !== i))}>
                    Quitar línea
                  </Boton>
                </div>
              </>
            ) : (
              <p>
                {l.item}: {l.quantity} {l.unit} · {l.notes}
              </p>
            )}
          </div>
        ))}
        {!lines.length && <p className="sutil">Sin líneas de material.</p>}
        {editable && (
          <div className="fila">
            <Boton
              onClick={() =>
                setLines([...lines, { item: '', quantity: '1', unit: 'ud', notes: '' }])
              }
            >
              Añadir material
            </Boton>
            <Boton
              variante="clave"
              cargando={busy}
              onClick={() =>
                guardar(() => api.put(`/kitchen/${ficha.id}/material`, { lines, version }))
              }
            >
              Guardar pedido
            </Boton>
          </div>
        )}
        <p>Adjuntos · PDF, JPG, PNG, XLSX o CSV · hasta 10 MB · máximo 5</p>
        {files.map((a) => (
          <div key={a.id} className="fila">
            <a href={`/api/attachments/${a.id}`}>{a.originalName}</a>
            {editable && (
              <Boton
                cargando={busy}
                onClick={() =>
                  guardar(() =>
                    api.del(`/kitchen/${ficha.id}/material/attachments/${a.id}?version=${version}`),
                  )
                }
              >
                Retirar adjunto
              </Boton>
            )}
          </div>
        ))}
        {editable && (
          <Campo etiqueta="Anexar pedido">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.csv"
              disabled={busy || files.length >= 5}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                if (file.size > 10 * 1024 * 1024) {
                  setError('El archivo supera 10 MB.');
                  return;
                }
                const form = new FormData();
                form.append('file', file);
                void guardar(() =>
                  api.post(`/kitchen/${ficha.id}/material/attachments?version=${version}`, form),
                );
              }}
            />
          </Campo>
        )}
      </div>
    </Tarjeta>
  );
}
